import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { packagesAPI, paymentsAPI, authAPI, dealsAPI } from '../lib/api';
import api from '../lib/api';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, 
  Truck, 
  CreditCard, 
  Copy, 
  ExternalLink,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Zap,
  Home,
  Store,
  FileText,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { packageStatusConfig, formatDate, formatCurrency, countryNames } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CustomsDeclarationForm from '../components/CustomsDeclarationForm';
import GroupageSection from '../components/GroupageSection';
import SubscriptionTeaser from '../components/SubscriptionTeaser';

export default function DashboardPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [myDeals, setMyDeals] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [packagesRes, addressRes, subRes, dealsRes] = await Promise.allSettled([
        packagesAPI.getMyPackages(),
        authAPI.getWarehouseAddress(),
        api.get('/subscription/me'),
        dealsAPI.myDeals(),
      ]);
      if (packagesRes.status === 'fulfilled') setPackages(packagesRes.value.data);
      if (addressRes.status === 'fulfilled') setWarehouseAddress(addressRes.value.data.address);
      if (subRes.status === 'fulfilled') setSubscription(subRes.value.data);
      if (dealsRes.status === 'fulfilled') setMyDeals(dealsRes.value.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle payment return (packages OR deals)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    const dealPaymentStatus = searchParams.get('deal_payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Paiement annulé');
    } else if (sessionId && dealPaymentStatus === 'success') {
      // Verify deal payment: find order via /my/deals then poll payment-status
      (async () => {
        try {
          // Refetch my deals to find latest pending
          const { data: deals } = await dealsAPI.myDeals();
          setMyDeals(deals);
          // Find any pending_payment
          const pending = deals.find(d => d.status === 'pending_payment');
          if (pending) {
            for (let i = 0; i < 5; i++) {
              const { data: ps } = await dealsAPI.paymentStatus(pending.id);
              if (ps.paid) {
                toast.success('✅ Réservation confirmée ! Votre place sur la palette est garantie.');
                fetchData();
                window.history.replaceState({}, '', '/dashboard?tab=deals');
                return;
              }
              await new Promise(r => setTimeout(r, 2000));
            }
            toast.info('Paiement en cours de vérification…');
          } else {
            toast.success('✅ Réservation confirmée !');
            window.history.replaceState({}, '', '/dashboard?tab=deals');
          }
        } catch (e) {
          toast.error('Vérification du paiement échouée');
        }
      })();
    } else if (dealPaymentStatus === 'cancelled') {
      toast.info('Réservation annulée');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Impossible de vérifier le statut du paiement');
      return;
    }

    try {
      const res = await paymentsAPI.getStatus(sessionId);
      
      if (res.data.payment_status === 'paid') {
        toast.success('Paiement confirmé ! Votre colis sera bientôt expédié.');
        fetchData();
        // Clear URL params
        window.history.replaceState({}, '', '/dashboard');
      } else if (res.data.status === 'expired') {
        toast.error('Session de paiement expirée');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handlePayment = async (packageId, shippingOptionId = null) => {
    setProcessingPayment(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await paymentsAPI.createCheckout(packageId, originUrl, shippingOptionId);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Erreur lors de la création du paiement');
      setProcessingPayment(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier !');
  };

  const pendingPackages = packages.filter(p => !p.paid && !p.group_id);
  const paidPackages = packages.filter(p => p.paid);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="customer-dashboard">
      <Navbar />
      
      <main className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading font-bold text-3xl text-slate-900">
              Bonjour, {user?.first_name} !
            </h1>
            <p className="text-slate-600 mt-1">Bienvenue dans votre espace client</p>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Customer Number */}
            <Card className="bg-white border-slate-200" data-testid="customer-number-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Numéro client</p>
                    <p className="font-heading font-bold text-2xl text-blue-600">{user?.customer_number}</p>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(user?.customer_number)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Copier"
                    data-testid="copy-customer-number-btn"
                  >
                    <Copy className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Pending Packages */}
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">En attente de paiement</p>
                    <p className="font-heading font-bold text-2xl text-slate-900">{pendingPackages.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Packages */}
            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-lime-100 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-lime-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total colis</p>
                    <p className="font-heading font-bold text-2xl text-slate-900">{packages.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Warehouse Address */}
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white mb-8" data-testid="warehouse-address-card">
            <CardContent className="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-300 mb-1">Adresse de livraison pour vos achats</p>
                      <p className="font-medium">{warehouseAddress}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-slate-900"
                    onClick={() => copyToClipboard(`${user?.first_name} ${user?.last_name} ${user?.customer_number}\n${warehouseAddress}`)}
                    data-testid="copy-warehouse-address-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier l'adresse
                  </Button>
                </div>
                
                {/* Important instruction */}
                <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4">
                  <p className="text-sm">
                    <strong className="text-blue-400">IMPORTANT :</strong> Lors de vos achats, indiquez toujours votre nom suivi de votre numéro client :
                  </p>
                  <p className="font-mono mt-2 text-lg">
                    {user?.first_name} {user?.last_name} <span className="text-blue-400 font-bold">{user?.customer_number}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-2">Sans ce numéro, nous ne pourrons pas identifier votre colis !</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription teaser (active card OR promo depending on state) */}
          <div className="mb-6">
            <SubscriptionTeaser subscription={subscription} packagesCount={packages.length} />
          </div>

          {/* Groupages section (shows grouped packages as a single card with payment button) */}
          <div className="mb-8">
            <GroupageSection />
          </div>

          {/* Packages Tabs */}
          <Tabs defaultValue={searchParams.get('tab') === 'deals' ? 'deals' : 'pending'} className="space-y-6">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="pending" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-pending">
                En attente ({pendingPackages.length})
              </TabsTrigger>
              <TabsTrigger value="paid" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-paid">
                Payés ({paidPackages.length})
              </TabsTrigger>
              <TabsTrigger value="deals" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900" data-testid="tab-deals">
                🎯 Mes Deals ({myDeals.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {pendingPackages.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      Aucun colis en attente
                    </h3>
                    <p className="text-slate-600">
                      Vos colis reçus à notre entrepôt apparaîtront ici.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {pendingPackages.map(pkg => (
                    <PackageCard 
                      key={pkg.id} 
                      pkg={pkg} 
                      onPay={handlePayment}
                      processingPayment={processingPayment}
                      onRefresh={fetchData}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="paid">
              {paidPackages.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      Aucun colis payé
                    </h3>
                    <p className="text-slate-600">
                      Vos colis payés et expédiés apparaîtront ici.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {paidPackages.map(pkg => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="deals">
              <MyDealsList deals={myDeals} customerNumber={user?.customer_number} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function DealPhoto({ url }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [url]);
  if (!url || broken) {
    return <div className="w-16 h-16 rounded bg-slate-100 flex-shrink-0 flex items-center justify-center text-2xl">📦</div>;
  }
  return <img src={url} alt="" className="w-16 h-16 rounded object-cover bg-slate-100 flex-shrink-0" onError={() => setBroken(true)} />;
}

function MyDealsList({ deals, customerNumber }) {
  if (!deals || deals.length === 0) {
    return (
      <Card className="bg-white border-slate-200">
        <CardContent className="p-12 text-center">
          <span className="text-5xl mb-4 inline-block">🎯</span>
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
            Aucune participation à un deal pour le moment
          </h3>
          <p className="text-slate-600 mb-4">
            Découvrez nos deals de groupage vers les DOM-TOM et économisez sur la livraison.
          </p>
          <Link to="/deals">
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="btn-browse-deals">
              🎯 Voir les deals ouverts
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const statusBadge = (status, paid) => {
    if (status === 'pending_payment' && !paid)
      return <Badge className="bg-orange-100 text-orange-800 border-orange-200">⏳ Paiement en attente</Badge>;
    if (status === 'declared')
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">✓ Réservé — En attente du colis</Badge>;
    if (status === 'in_transit')
      return <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200">🚚 En transit</Badge>;
    if (status === 'received')
      return <Badge className="bg-green-100 text-green-800 border-green-200">📦 Reçu chez ISM</Badge>;
    if (status === 'assigned_pallet')
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200">🎯 Sur palette</Badge>;
    if (status === 'shipped')
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">✈️ Expédié</Badge>;
    if (status === 'delivered')
      return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✅ Livré</Badge>;
    if (status === 'refused')
      return <Badge className="bg-red-100 text-red-800 border-red-200">✕ Refusé</Badge>;
    if (status === 'cancelled')
      return <Badge className="bg-slate-100 text-slate-600 border-slate-200">Annulé</Badge>;
    return <Badge>{status}</Badge>;
  };

  return (
    <div className="space-y-3" data-testid="my-deals-list">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
        💡 <strong>Comment ça marche :</strong> Vous avez réservé votre place sur la palette. Achetez maintenant chez le marchand avec votre <strong>n° client {customerNumber}</strong> sur l'étiquette, et suivez la progression ici.
      </div>
      {deals.map(d => {
        const price = d.amount_expected || d.dp?.fixed_price || 0;
        return (
          <Card key={d.id} className="bg-white border-slate-200" data-testid={`deal-order-${d.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <DealPhoto url={d.product?.photos?.[0]} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                    <h4 className="font-heading font-bold text-slate-900 truncate">
                      {d.product?.title || 'Produit'}
                    </h4>
                    {statusBadge(d.status, d.paid)}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {d.deal?.name} · {d.destination?.flag} {d.destination?.name || d.deal?.destination_code}
                  </p>
                  <div className="flex flex-wrap gap-4 text-xs text-slate-700">
                    <span>💳 <strong>{price.toFixed(2)} €</strong> {d.paid ? '(payé ✓)' : '(à payer)'}</span>
                    {d.tracking_number && <span>📮 <strong>{d.tracking_number}</strong></span>}
                    {d.merchant_order_number && <span>🛒 <strong>{d.merchant_order_number}</strong></span>}
                    {d.received_at && <span>📦 reçu le {new Date(d.received_at).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  {d.status === 'declared' && d.paid && (
                    <p className="text-xs text-slate-500 mt-2 italic">
                      👉 Assurez-vous que votre n° client <strong>{customerNumber}</strong> figure sur l'adresse d'expédition chez le marchand.
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


function PackageCard({ pkg, onPay, processingPayment, onRefresh }) {
  const statusConfig = packageStatusConfig[pkg.status] || packageStatusConfig.received;
  const [selectedOption, setSelectedOption] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showCustomsForm, setShowCustomsForm] = useState(false);
  const [pendingShippingOption, setPendingShippingOption] = useState(null);

  const availableOptions = pkg.available_shipping_options || [];
  const chosenOption = pkg.shipping_option;
  const hasCustomsDeclaration = !!pkg.customs_declaration;

  // Icon based on carrier
  const getOptionIcon = (option) => {
    if (option.is_express) return Zap;
    if (option.name.toLowerCase().includes('relais') || option.name.toLowerCase().includes('retrait')) return Store;
    return Truck;
  };

  const handleSelectAndPay = (option) => {
    setSelectedOption(option);
    if (!hasCustomsDeclaration) {
      // Show customs form first
      setPendingShippingOption(option);
      setShowCustomsForm(true);
    } else {
      // Declaration already done, go to payment
      onPay(pkg.id, option.id);
    }
  };

  const handleCustomsComplete = () => {
    setShowCustomsForm(false);
    // Refresh to update package data, then proceed to payment
    if (onRefresh) onRefresh();
    if (pendingShippingOption) {
      onPay(pkg.id, pendingShippingOption.id);
    }
  };

  return (
    <>
    <Card className="bg-white border-slate-200 overflow-hidden" data-testid={`package-card-${pkg.id}`}>
      <CardContent className="p-0">
        <div className="flex flex-col lg:flex-row">
          {/* Photo */}
          {pkg.photo_url && (
            <div className="lg:w-48 h-48 lg:h-auto bg-slate-100 flex-shrink-0">
              <img 
                src={pkg.photo_url} 
                alt={pkg.description}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 p-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="font-heading font-bold text-lg text-slate-900">
                    {pkg.description}
                  </h3>
                  <Badge className={statusConfig.color}>
                    {statusConfig.label}
                  </Badge>
                  {!pkg.paid && hasCustomsDeclaration && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      <FileText className="w-3 h-3 mr-1" />
                      Douane OK
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Poids</p>
                    <p className="font-medium text-slate-900">{pkg.weight} kg</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Dimensions</p>
                    <p className="font-medium text-slate-900">{pkg.length}x{pkg.width}x{pkg.height} cm</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Destination</p>
                    <p className="font-medium text-slate-900">{countryNames[pkg.destination_country] || pkg.destination_country}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Reçu le</p>
                    <p className="font-medium text-slate-900">{formatDate(pkg.created_at)}</p>
                  </div>
                </div>

                {/* Show chosen shipping option if paid */}
                {pkg.paid && chosenOption && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {chosenOption.name} ({chosenOption.carrier})
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      Délai : {chosenOption.delivery_time}
                    </p>
                  </div>
                )}

                {pkg.tracking_url && (
                  <a 
                    href={pkg.tracking_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Truck className="w-4 h-4" />
                    Suivre le colis
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Price & Payment */}
              <div className="text-right min-w-[200px]">
                {!pkg.paid && availableOptions.length > 0 ? (
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Choisissez votre expédition</p>
                    
                    {!showOptions ? (
                      <div>
                        <p className="font-heading font-bold text-xl text-slate-900 mb-1">
                          à partir de {formatCurrency(availableOptions[0]?.price || pkg.shipping_cost)}
                        </p>
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => setShowOptions(true)}
                          disabled={processingPayment === pkg.id}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Voir les options
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {availableOptions.map((option, index) => {
                          const Icon = getOptionIcon(option);
                          return (
                            <button
                              key={option.id}
                              onClick={() => handleSelectAndPay(option)}
                              disabled={processingPayment === pkg.id}
                              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                processingPayment === pkg.id && selectedOption?.id === option.id
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 hover:border-blue-400 bg-white'
                              }`}
                              data-testid={`shipping-option-${option.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-4 h-4 ${option.is_express ? 'text-orange-500' : 'text-slate-600'}`} />
                                  <div>
                                    <p className="font-medium text-sm text-slate-800">{option.name}</p>
                                    <p className="text-xs text-slate-500">{option.delivery_time}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-blue-600">{formatCurrency(option.price)}</p>
                                  {index === 0 && (
                                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                      Meilleur prix
                                    </span>
                                  )}
                                  {option.is_express && index !== 0 && (
                                    <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                      Express
                                    </span>
                                  )}
                                </div>
                              </div>
                              {processingPayment === pkg.id && selectedOption?.id === option.id && (
                                <div className="mt-2 flex items-center justify-center text-blue-600">
                                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                  <span className="text-sm">Redirection...</span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => setShowOptions(false)}
                          className="text-sm text-slate-500 hover:text-slate-700 mt-1"
                        >
                          ← Masquer les options
                        </button>
                      </div>
                    )}
                  </div>
                ) : !pkg.paid ? (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Frais d'expédition</p>
                    <p className="font-heading font-bold text-2xl text-slate-900">
                      {formatCurrency(pkg.shipping_cost)}
                    </p>
                    <Button 
                      className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        if (!hasCustomsDeclaration) {
                          setShowCustomsForm(true);
                        } else {
                          onPay(pkg.id, null);
                        }
                      }}
                      disabled={processingPayment === pkg.id}
                      data-testid={`pay-btn-${pkg.id}`}
                    >
                      {processingPayment === pkg.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Traitement...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Payer maintenant
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Frais d'expédition</p>
                    <p className="font-heading font-bold text-2xl text-slate-900">
                      {formatCurrency(pkg.shipping_cost)}
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-lime-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Payé</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Customs Declaration Dialog */}
    <Dialog open={showCustomsForm} onOpenChange={setShowCustomsForm}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Déclaration douanière
          </DialogTitle>
        </DialogHeader>
        <CustomsDeclarationForm
          packageId={pkg.id}
          packageDescription={pkg.description}
          existingDeclaration={pkg.customs_declaration}
          onComplete={handleCustomsComplete}
          onCancel={() => {
            setShowCustomsForm(false);
            setPendingShippingOption(null);
            setSelectedOption(null);
          }}
        />
      </DialogContent>
    </Dialog>
    </>
  );
}
